import React, { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface Worldview {
  id: string;
  name: string;
  description: string;
}

const worldviews: Worldview[] = [
  { id: "atheist", name: "Atheist", description: "Someone who believes there is no god or higher power" },
  { id: "muslim", name: "Muslim", description: "A follower of Islam who believes in Allah and Muhammad as his prophet" },
  { id: "jew", name: "Jew", description: "A person who follows Judaism and believes in one God" },
  { id: "hindu", name: "Hindu", description: "A follower of Hinduism with beliefs in multiple deities and reincarnation" },
  { id: "mormon", name: "Mormon", description: "A member of the Church of Jesus Christ of Latter-day Saints" },
  { id: "jehovah", name: "Jehovah's Witness", description: "A member who believes in Jehovah as the only true God" },
  { id: "blackhebrew", name: "Black Hebrew Israelite", description: "One who believes Black Americans are descendants of ancient Israelites" },
  { id: "newage", name: "New Age / Spiritual", description: "Someone with eclectic spiritual beliefs focused on self-discovery" },
  { id: "universalist", name: "Universalist", description: "Someone who believes all people will eventually be saved" },
];

interface WorldviewSelectorProps {
  onWorldviewSelect: (worldview: string) => void;
}

export const WorldviewSelector: React.FC<WorldviewSelectorProps> = ({ onWorldviewSelect }) => {
  useEffect(() => {
    // Ensure proper rendering in both split and full-screen modes
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'auto'
      });
      // Force rerender to ensure visibility
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }, []);

  return (
    <Card className="my-8 border-heaven-gold/20">
      <CardHeader>
        <CardTitle className="text-3xl text-center font-bold text-[#403E43]">
          Choose a worldview to engage with
        </CardTitle>
        <CardDescription className="text-center">
          Select who you want to talk to from the options below
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {worldviews.map((worldview) => (
            <Button
              key={worldview.id}
              variant="outline"
              className="h-auto min-h-[170px] flex flex-col items-start p-4 border border-heaven-gold/20 hover:bg-heaven-gold/10 hover:border-heaven-gold/50"
              onClick={() => onWorldviewSelect(worldview.id)}
              type="button" // Explicitly set type to prevent form submission
            >
              <span className="font-semibold mb-2 w-full text-left">{worldview.name}</span>
              <span className="text-sm text-gray-600 text-left w-full whitespace-normal break-words line-clamp-2 overflow-hidden">
                {worldview.description}
              </span>
            </Button>
          ))}
        </div>
        <div className="mt-8 bg-blue-50 p-4 rounded-lg border border-blue-100">
          <h4 className="font-semibold text-black mb-2">🧠 Tips for Using This Tool:</h4>
          <ul className="list-disc list-inside text-sm text-black space-y-1">
            <li>Lead with love, not arrogance</li>
            <li>Use short Scripture verses—don't preach sermons</li>
            <li>Ask questions like Jesus did</li>
            <li>Stay focused on Jesus, not just theology</li>
            <li>Don't forget the gospel message—life, death, resurrection, and salvation by grace</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
